import {
  AfterContentChecked,
  Component,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { DetailsContainer } from 'src/app/core/containers/details-container';
import { dynamicSubfieldSort } from 'src/app/core/functions/common-functions';
import { getUserTimezone } from 'src/app/core/functions/date-functions';
import { DirtyStatus } from 'src/app/model/common/dirty-status';
import { ShiftTurnover } from 'src/app/model/shift/shift-turnover';
import { FacilityBuilderService } from 'src/app/services/builder/facility-builder.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { ShiftTurnoverApiService } from 'src/app/services/api/shift-turnover-api.service';
import { ShiftTurnoverBuilderService } from 'src/app/services/builder/shift-turnover-builder.service';
import { ShiftTurnoverTableService } from 'src/app/services/table/shift-turnover-table.service';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { ProgressSpinnerComponent } from '../../core/components/progress-spinner.component';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { Panel } from '@openng/optimus-ui/panel';
import { DatePipe } from '@angular/common';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-mc-shift-turnover-print-container',
  templateUrl: './mc-shift-turnover-print-container.component.html',
  styleUrls: ['./mc-shift-turnover-print-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ProgressSpinnerComponent,
    ButtonDirective,
    TranslateDirective,
    Panel,
    DatePipe,
    TranslatePipe,
  ],
})
export class McShiftTurnoverPrintContainerComponent
  extends DetailsContainer
  implements OnInit, OnDestroy, AfterContentChecked
{
  protected _logAndMessage = inject(LogAndMessageService);
  protected _builder = inject(ShiftTurnoverBuilderService);
  protected _api = inject(ShiftTurnoverApiService);
  protected _facilityBuilder = inject(FacilityBuilderService);
  protected _shiftTable = inject(ShiftTurnoverTableService);

  shiftTurnover: ShiftTurnover | null = null;
  incomingAnalyst: string | null = null;
  departingAnalyst: string | null = null;
  statusValue: string = '';
  detailsObj: any;
  itemsArray: any[] = [];
  mitArray: any[] = [];
  timezone: string | null = null;

  constructor() {
    super();
  }

  ngOnInit() {
    this.timezone = getUserTimezone();
    this.setupTabClosingSubscription();
    this.initialize();
  }

  ngAfterContentChecked(): void {
    const activeId = this.id;
    if (
      !isNullOrUndefined(activeId) &&
      this.shiftTurnover &&
      this.shiftTurnover.id !== activeId &&
      !this.doCheckComplete
    ) {
      this.doCheckComplete = true;
      this._shiftTable.setSelected(null);
      this.initialize();
    }
  }

  ngOnDestroy(): void {
    if (this.tabClosingSub) {
      this.tabClosingSub.unsubscribe();
    }
  }

  initialize() {
    const id = this.id;

    if (!id) {
      this.shiftTurnover = this._shiftTable.getSelected();
      // no id and nothing cached, go back to search.
      if (!this.shiftTurnover) {
        this._logAndMessage.errorLogOnly('MC shift turnover was null.');
        const index = this._tabService.getActiveIndex();
        this._tabService.closeTab(index);
      }
      this.updateTabHeader();
      this.setupScreen();
    } else {
      this._api.getShiftTurnover(id).subscribe((turnover) => {
        if (turnover) {
          this.shiftTurnover = turnover;
          this.doCheckComplete = false;
          this.updateTabHeader();
          this.setupScreen();
        } else {
          // nothing found, go back to search.
          this._logAndMessage.errorLogOnly(
            'Could not find MC shift turnover with ID: ' + id
          );
          const index = this._tabService.getActiveIndex();
          this._tabService.closeTab(index);
        }
      });
    }
  }

  setupScreen() {
    const inArray = this.shiftTurnover?.ShiftDetails.filter(
      (d) => d.incomingDepartingFlag === 'I'
    );
    const depArray = this.shiftTurnover?.ShiftDetails.filter(
      (d) => d.incomingDepartingFlag === 'D'
    );
    this.statusValue = this._builder.getStatusLabelForStatus(
      this.shiftTurnover!.status
    );

    this.incomingAnalyst =
      inArray!.length > 0
        ? inArray![0].Analyst!.firstName + ' ' + inArray![0].Analyst!.lastName
        : null;
    this.departingAnalyst =
      depArray!.length > 0
        ? depArray![0].Analyst!.firstName + ' ' + depArray![0].Analyst!.lastName
        : null;

    let incoming,
      departing = null;
    if (
      this.shiftTurnover?.ShiftDetails &&
      this.shiftTurnover?.ShiftDetails.length > 0
    ) {
      incoming = this.shiftTurnover.ShiftDetails.filter(
        (d) => d.incomingDepartingFlag === 'I'
      )[0];
      departing = this.shiftTurnover.ShiftDetails.filter(
        (d) => d.incomingDepartingFlag === 'D'
      )[0];
      this.detailsObj = {
        incomingAdditionalComments: incoming.additionalComments,
        incomingFitFlag: incoming.fitFlag,
        incomingScadaFlag: incoming.scadaFlag,
        incomingReviewedAllSections: incoming.reviewedAllSections,
        departingAdditionalComments: departing.additionalComments,
        departingFitFlag: departing.fitFlag,
        departingScadaFlag: departing.scadaFlag,
      };

      const depItemArray = departing.ShiftTurnoverItems.filter(
        (co: any) => co.dirtyStatus !== DirtyStatus.DELETED
      ).sort(dynamicSubfieldSort('ShiftItem', 'sortOrder', 1));

      this.itemsArray = [];
      for (let i = 0; i < depItemArray.length; i++) {
        const departingItem = depItemArray[i];
        this.itemsArray.push({
          incomingId: null,
          departingId: departingItem.id,
          itemName: departingItem.ShiftItem?.name,
          incomingShiftItemId: null,
          departingShiftItemId: departingItem.ShiftItem?.id,
          incomingComments: null,
          departingComments: departingItem.comments,
          incomingTurnoverFlag: null,
          departingTurnoverFlag: departingItem.turnoverFlag,
        });
      }

      const depMitArray = departing.ShiftTurnoverMitigations.filter(
        (co: any) => co.dirtyStatus !== DirtyStatus.DELETED
      ).sort(dynamicSubfieldSort('ShiftMitigation', 'sortOrder', 1));
      this.mitArray = [];
      for (let j = 0; j < depMitArray.length; j++) {
        const mit = depMitArray[j];
        this.mitArray.push({
          id: mit.id,
          mitigationId: mit.ShiftMitigation?.id,
          mitigationName: mit.ShiftMitigation?.name,
          turnoverFlag: mit.turnoverFlag,
        });
      }
    }
  }

  print() {
    // This used to use printjs, but we ran into some issues with lines
    // overlapping each other in the migration to angular 13, so now
    // we use @media print styling to only show what is needed
    // and use the default window.print() to generate the document!
    window.print();
  }

  async updateTabHeader(): Promise<void> {
    const label = await firstValueFrom(
      this._translateService.get('COMMON.LABEL.PRINT')
    );
    const header =
      label +
      ': ' +
      this.shiftTurnover?.shift +
      ' - ' +
      this.shiftTurnover?.shiftDate.toISOString();
    this._tabService.updateActiveTabLabel(header);
  }
}
